import { Autocomplete, Box, TextField } from "@mui/material";
import { BANNER_CONTROL_HEIGHT } from "../../straightener-common/constants";
import type { StraightenerRecipeVersion } from "../../straightener-recipes/models/StraightenerRecipe";

// Recipe identity = name + crew; the version number is secondary (shown last,
// de-emphasized). Kept in the option label so a search on "v2" still matches.
const recipeLabel = (v: StraightenerRecipeVersion): string =>
	`${v.Name ?? ""}${v.Crew ? ` · Crew ${v.Crew}` : ""}${
		v.IsActive ? "" : " · inactive"
	}`;
const versionLabel = (v: StraightenerRecipeVersion): string =>
	`${recipeLabel(v)} · v${v.Version}`;

interface Props {
	// All recipe versions of the current product (the search pool).
	versions: StraightenerRecipeVersion[];
	// The selected recipe (defaults to the current recipe). Null => none.
	valueId: number | null;
	// The product's current recipe, flagged "(default)" in the list so picking
	// another one is visibly a departure from it rather than an equal choice.
	currentId?: number | null;
	onSelect: (recipeId: number) => void;
}

// The recipe picker shown in the banner's RECIPE slot, in place of the plain
// current-recipe text. Searchable over the current product's versions.
export const RecipeSelect = ({
	versions,
	valueId,
	currentId,
	onSelect,
}: Props) => {
	const value = versions.find((v) => v.Id === valueId) ?? null;

	return (
		<Autocomplete
			size="small"
			options={versions}
			value={value}
			onChange={(_, v) => v && onSelect(v.Id)}
			getOptionLabel={versionLabel}
			isOptionEqualToValue={(a, b) => a.Id === b.Id}
			disabled={versions.length === 0}
			sx={{
				width: 400,
				// A recipe is always assigned — no un-assign, so hide the clear (X).
				"& .MuiAutocomplete-clearIndicator": { display: "none" },
				// Every box in the chain is pinned to the same height so the control
				// lines up with the buttons beside it. `display: flex` is the load
				// bearing part: the TextField inside is inline-flex, and an inline
				// child sits on the parent's text baseline, which reserves room for
				// descenders and pushes the visible box down even when the heights
				// already match. Making the root a flex container removes that line
				// box, so what you see is exactly BANNER_CONTROL_HEIGHT with nothing
				// above or below it.
				height: BANNER_CONTROL_HEIGHT,
				display: "flex",
				alignItems: "stretch",
				m: 0,
				"& .MuiFormControl-root": { height: "100%", width: "100%", m: 0 },
				"& .MuiOutlinedInput-root": {
					height: "100%",
					minHeight: 0,
					py: 0,
					boxSizing: "border-box",
				},
			}}
			renderOption={(props, v) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<Box component="li" key={key} {...rest}>
						{recipeLabel(v)}
						<Box component="span" sx={{ color: "text.disabled", ml: 0.5 }}>
							· v{v.Version}
						</Box>
						{v.Id === currentId ? (
							<Box component="span" sx={{ color: "text.disabled", ml: 0.5 }}>
								- (default)
							</Box>
						) : null}
					</Box>
				);
			}}
			renderInput={(params) => (
				<TextField {...params} placeholder="Select recipe" />
			)}
		/>
	);
};
